import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n";
import { MaskWord } from "./Reveal";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const h = t.hero;

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      {/* Background image with parallax + overlay */}
      <motion.div style={{ y: yImg }} className="absolute inset-0 -top-20 -bottom-20 z-0">
        <img
          src={COMPANY.photos.aisle}
          alt="Magacinski prostor — Kruška Distribucija"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
      </motion.div>

      {/* Spot glows */}
      <div className="spot-glow w-[500px] h-[500px] bg-[#D4AF37]/20 -top-40 -right-20" />
      <div className="spot-glow w-[400px] h-[400px] bg-[#1E5631]/25 bottom-0 left-10" />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 max-w-[1400px] mx-auto w-full px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center"
      >
        <div className="lg:col-span-8">
          <MaskWord delay={0.2}>
            <span className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-mono uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-7">
              {h.badge}
            </span>
          </MaskWord>

          <h1 className="font-display font-medium tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-[7.5vw] text-white">
            <MaskWord delay={0.35}>{h.line1}</MaskWord>
            <MaskWord delay={0.5} className="text-[#D4AF37]">{h.line2}</MaskWord>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-300"
          >
            {h.lead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#inquiry"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-black font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all"
            >
              {h.ctaPrimary}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#categories"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 text-white font-semibold hover:border-[#D4AF37]/60 hover:bg-white/5 transition-all"
            >
              {h.ctaSecondary}
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8"
          >
            {[
              [h.stat1, h.stat1l],
              [h.stat2, h.stat2l],
              [h.stat3, h.stat3l],
            ].map(([v, l], i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <div className="font-display text-3xl sm:text-4xl font-bold text-white">{v}</div>
                <div className="text-xs text-zinc-400 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Floating logo badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:flex lg:col-span-4 justify-center"
        >
          <motion.img
            src={COMPANY.logo}
            alt="Kruška Distribucija"
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="w-72 h-72 xl:w-80 xl:h-80 rounded-full object-cover ring-1 ring-[#D4AF37]/30 shadow-[0_0_80px_rgba(212,175,55,0.25)]"
          />
        </motion.div>
      </motion.div>

      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-zinc-500 hidden sm:block"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.a>
    </section>
  );
};
